import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { homeFor } from "@/nav";
import { DtHome, DtRetailers, DtFundTransfer, DtTransferHistory, DtNetworkReport, DtTransferAll, DtFundSummary, DtRtStatement, DtEarnings, DtPendingDmt, DtSalesReport } from "@/pages/Distributor";
import ForgotPassword from "@/pages/ForgotPassword";
import Login from "@/pages/Login";
import SiteLanding from "@/pages/site/Landing";
import AboutPage from "@/pages/site/AboutPage";
import LegalPage from "@/pages/site/LegalPage";
import ServiceDetailPage from "@/pages/site/ServiceDetailPage";
import Lenis from "lenis";
import Dashboard from "@/pages/Dashboard";
import { Recharge, CreditCard, CashDeposit, PanVerify } from "@/pages/Services";
import Bbps from "@/pages/Bbps";
import Dmt from "@/pages/Dmt";
import { Aeps, AepsOnboarding } from "@/pages/Aeps";
import { FundRequest, FundHistory, MoveToBank, WalletTransfer, PaymentGateway, QrCollect } from "@/pages/Wallet";
import { ServiceReport, Statement, Commission, Slabs, PayoutReport } from "@/pages/Reports";
import { SupportList, AddTicket, TicketDetail } from "@/pages/Support";
import { Profile, Kyc, Security, IdCard, StaticPage } from "@/pages/Profile";
import { AdminCharges } from "@/pages/Charges";
import { AdminLimits } from "@/pages/Limits";
import ReconPage from "@/pages/recon/ReconPage";
import MobileApp from "@/mobile/MobileApp";
import { AdminApprovals, AdminStatement, CompanyBanks, SalesReport, FundSummary, AdminLogs, SubAdmins } from "@/pages/AdminExtra";
import { AdminHome, AdminUsers, AdminFunds, AdminTxns, AdminTickets, AdminNews, AdminCallbacks, AdminServices } from "@/pages/Admin";

function Protected({ roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-sm text-slate-500" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <AppLayout />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return <Login />;
}

const REPORT_TYPES = ["recharge", "bbps", "dmt", "aeps", "settlement", "pg", "all"];

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let f;
    const raf = (t) => { lenis.raf(t); f = requestAnimationFrame(raf); };
    f = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(f); lenis.destroy(); };
  }, []);

  return (
    <AuthProvider>
      <div className="grain"><BrowserRouter>
        <Routes>
          <Route path="/" element={<SiteLanding />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<LegalPage kind="terms" title="Terms & Conditions" subtitle="The agreement between you and ShubhPay for use of our platform and services." />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" title="Privacy Policy" subtitle="How we collect, use, protect and retain your data across the ShubhPay platform." />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/secure/:key" element={<LoginRoute />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route element={<Protected roles={["retailer"]} />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/recharge/mobile" element={<Recharge kind="mobile" />} />
            <Route path="/recharge/dth" element={<Recharge kind="dth" />} />
            <Route path="/bbps" element={<Bbps />} />
            <Route path="/credit-card" element={<CreditCard />} />
            <Route path="/dmt" element={<Dmt />} />
            <Route path="/pending-dmt" element={<ServiceReport type="pending-dmt" />} />
            <Route path="/aeps" element={<Aeps />} />
            <Route path="/aadhaar-pay" element={<Aeps aadhaarPay />} />
            <Route path="/aeps/onboarding" element={<AepsOnboarding />} />
            <Route path="/cash-deposit" element={<CashDeposit />} />
            <Route path="/move-to-bank" element={<MoveToBank />} />
            <Route path="/wallet-transfer" element={<WalletTransfer />} />
            <Route path="/payment-gateway" element={<PaymentGateway />} />
            <Route path="/qr-collect" element={<QrCollect />} />
            <Route path="/pan-verify" element={<PanVerify />} />
            {REPORT_TYPES.map((t) => <Route key={t} path={`/reports/${t}`} element={<ServiceReport type={t} />} />)}
            <Route path="/reports/move-to-bank" element={<PayoutReport scope="retailer" />} />
            <Route path="/commission" element={<Commission />} />
            <Route path="/slabs" element={<Slabs />} />
            <Route path="/id-card" element={<IdCard />} />
            <Route path="/page/:slug" element={<StaticPage />} />
          </Route>
          <Route element={<Protected roles={["admin", "subadmin"]} />}>
            <Route path="/admin" element={<AdminHome />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:role" element={<AdminUsers />} />
            <Route path="/admin/fund-requests" element={<AdminFunds />} />
            <Route path="/admin/transactions" element={<AdminTxns />} />
            <Route path="/admin/tickets" element={<AdminTickets />} />
            <Route path="/admin/news" element={<AdminNews />} />
            <Route path="/admin/callbacks" element={<AdminCallbacks />} />
            <Route path="/admin/services" element={<AdminServices />} />
            <Route path="/admin/charges" element={<AdminCharges />} />
            <Route path="/admin/limits" element={<AdminLimits />} />
            <Route path="/admin/statement" element={<AdminStatement />} />
            <Route path="/admin/approvals" element={<AdminApprovals />} />
            <Route path="/admin/company-banks" element={<CompanyBanks />} />
            <Route path="/admin/sales-report" element={<SalesReport />} />
            <Route path="/admin/fund-summary" element={<FundSummary />} />
            <Route path="/admin/logs" element={<AdminLogs />} />
            <Route path="/admin/subadmins" element={<SubAdmins />} />
            <Route path="/admin/move-to-bank" element={<PayoutReport scope="admin" />} />
            <Route path="/admin/recon" element={<ReconPage />} />
          </Route>
          <Route element={<Protected roles={["distributor", "super", "sr"]} />}>
            <Route path="/dt" element={<DtHome />} />
            <Route path="/dt/retailers" element={<DtRetailers />} />
            <Route path="/dt/fund-transfer" element={<DtFundTransfer />} />
            <Route path="/dt/transfer-history" element={<DtTransferHistory />} />
            <Route path="/dt/network-report" element={<DtNetworkReport />} />
            <Route path="/dt/transfer-all" element={<DtTransferAll />} />
            <Route path="/dt/fund-summary" element={<DtFundSummary />} />
            <Route path="/dt/rt-statement" element={<DtRtStatement />} />
            <Route path="/dt/earnings" element={<DtEarnings />} />
            <Route path="/dt/pending-dmt" element={<DtPendingDmt />} />
            <Route path="/dt/sales-report" element={<DtSalesReport />} />
            <Route path="/dt/move-to-bank" element={<PayoutReport scope="dt" />} />
          </Route>
          <Route element={<Protected roles={["retailer", "distributor", "super", "sr"]} />}>
            <Route path="/fund-request" element={<FundRequest />} />
            <Route path="/fund-history" element={<FundHistory />} />
            <Route path="/reports/statement" element={<Statement />} />
            <Route path="/support" element={<SupportList />} />
            <Route path="/support/new" element={<AddTicket />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/kyc" element={<Kyc />} />
          </Route>
          <Route element={<Protected />}>
            <Route path="/security" element={<Security />} />
            <Route path="/support/:id" element={<TicketDetail />} />
          </Route>
          <Route path="/app/*" element={<MobileApp />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter></div>
      {window.location.pathname.startsWith("/app") ? <Toaster richColors position="top-center" duration={2500} /> : <Toaster richColors position="top-right" duration={5000} />}
    </AuthProvider>
  );
}

export default App;
